/**
 * Defines the order in which game systems should be cleaned up.
 * Systems are cleaned in the order listed here.
 * 
 * IMPORTANT: Some systems are PERSISTENT and should NOT be cleaned up:
 * - InputHandler: Maintains event listeners for zoom, double-click, camera drag
 * - AnimationManager: Stores global animation definitions used across rooms
 * 
 * The order is important to avoid dependency issues:
 * 1. UI and visual systems first (no dependencies)
 * 2. Game logic systems
 * 3. Core systems last
 * 
 * AI ASSISTANT NOTE: When adding new systems, consider their
 * dependencies and add them to the appropriate position in this list.
 * Systems that depend on others should be cleaned up before their dependencies.
 * DO NOT add 'input' or 'animation' - they are persistent systems!
 */
export const SYSTEM_CLEANUP_ORDER = [
  // UI and visual systems first (no dependencies on other systems)
  'messages',      // UI messages have no dependencies
  'nameTags',      // Name tags can be removed anytime
  'decorations',   // Decorations have no dependencies on other systems

  // Game logic systems (may depend on world/player)
  'privateAreas',  // Clean private area logic before world
  'zones',         // Zone interactions before world
  'interaction',   // Player interactions before player system

  // Core systems last (many things depend on these)
  'world',         // World and tilemap (many systems use this)
  'player',        // Player system (interactions depend on this)
  'camera',        // Camera controller (needs reset between rooms)
  'pathfinder',    // Pathfinding (if exists)
  'room'           // Room manager last (manages overall state)

  // EXCLUDED PERSISTENT SYSTEMS (DO NOT ADD):
  // 'input'       - Persistent: maintains zoom, double-click, camera drag
  // 'animation'   - Persistent: global animation definitions
] as const;

/**
 * Systems that persist across room transitions and should NEVER be cleaned up
 * These systems maintain global state or event listeners that work across all rooms
 */
export const PERSISTENT_SYSTEMS = [
  'input',      // InputHandler - maintains zoom, double-click movement, camera drag
  'animation'   // AnimationManager - stores global animation definitions
] as const;

/**
 * Type-safe system names based on the cleanup order
 */
export type SystemName = typeof SYSTEM_CLEANUP_ORDER[number];

/**
 * Type for persistent system names
 */
export type PersistentSystemName = typeof PERSISTENT_SYSTEMS[number];

/**
 * Interface for a cleanable system
 */
export interface CleanableSystem {
  cleanup(): void | Promise<void>;
}

/**
 * Type guard to check if an object is a cleanable system
 */
export function isCleanableSystem(obj: any): obj is CleanableSystem {
  return obj && typeof obj.cleanup === 'function';
}

/**
 * Configuration for cleanup behavior
 */
export interface CleanupConfig {
  /** Enable leak detection in development */
  enableLeakDetection?: boolean;
  /** Log cleanup timing information */
  logTiming?: boolean;
  /** Maximum time allowed for cleanup (ms) */
  maxCleanupTime?: number;
  /** Systems to skip during cleanup */
  skipSystems?: SystemName[];
}

/**
 * Default cleanup configuration
 */
export const DEFAULT_CLEANUP_CONFIG: CleanupConfig = {
  enableLeakDetection: import.meta.env.DEV === true,
  logTiming: import.meta.env.DEV === true,
  maxCleanupTime: 5000, // 5 seconds max
  skipSystems: []
};

/**
 * Helper to get systems in cleanup order from a systems object
 * 
 * @example
 * ```typescript
 * const orderedSystems = getSystemsInCleanupOrder(gameScene.systems);
 * for (const { name, system } of orderedSystems) {
 *   system.cleanup();
 * }
 * ```
 */
export function getSystemsInCleanupOrder<T extends Record<string, any>>(
  systems: T
): Array<{ name: SystemName; system: CleanableSystem }> {
  const ordered: Array<{ name: SystemName; system: CleanableSystem }> = [];
  
  for (const systemName of SYSTEM_CLEANUP_ORDER) {
    const system = systems[systemName];
    if (system && isCleanableSystem(system)) {
      ordered.push({ name: systemName, system });
    }
  }
  
  return ordered;
}

/**
 * Validate that all systems in an object are in the cleanup order
 * Useful for development-time checks
 * NOTE: Persistent systems (input, animation) are excluded from validation
 * 
 * @example
 * ```typescript
 * if (import.meta.env.DEV) {
 *   const validation = validateSystemsInCleanupOrder(gameScene.systems);
 *   if (!validation.valid) {
 *     console.warn('Missing systems in cleanup order:', validation.missing);
 *   }
 * }
 * ```
 */
export function validateSystemsInCleanupOrder(
  systems: Record<string, any>
): { valid: boolean; missing: string[] } {
  const systemNames = Object.keys(systems).filter(key => 
    systems[key] && isCleanableSystem(systems[key])
  );
  
  // Filter out persistent systems that shouldn't be in cleanup order
  const missing = systemNames.filter(name => 
    !SYSTEM_CLEANUP_ORDER.includes(name as SystemName) &&
    !PERSISTENT_SYSTEMS.includes(name as PersistentSystemName)
  );
  
  return {
    valid: missing.length === 0,
    missing
  };
}

/**
 * Check if a system is persistent (should not be cleaned up)
 * @param systemName - Name of the system to check
 * @returns true if the system is persistent
 */
export function isPersistentSystem(systemName: string): boolean {
  return PERSISTENT_SYSTEMS.includes(systemName as PersistentSystemName);
}

/**
 * Get cleanup priority for a system (lower number = cleaned up first)
 * Returns -1 if system is not in the cleanup order
 */
export function getCleanupPriority(systemName: string): number {
  const index = SYSTEM_CLEANUP_ORDER.indexOf(systemName as SystemName);
  return index;
}

/**
 * Sort an array of system names by cleanup order
 */
export function sortByCleanupOrder(systemNames: string[]): string[] {
  return [...systemNames].sort((a, b) => {
    const priorityA = getCleanupPriority(a);
    const priorityB = getCleanupPriority(b);
    
    // If either is not in the list, put it at the end
    if (priorityA === -1) return 1;
    if (priorityB === -1) return -1;
    
    return priorityA - priorityB;
  });
}